import moment from 'moment';
import qs, {
    parse,
    stringify,
} from 'query-string';

/**
 * @param {Object} history object - react-router or some such
 * @param {String} fallbackURL - go here if nothing is present in the history stack
 */
function goBackOrFallback(history, fallbackURL) {
    if (!history.goBack()) {
        history.push(fallbackURL);
    }
}

export function addQueryParamsToUrl(url, params = {}) {
    if (!url
        || typeof url !== 'string'
        || !params
        || typeof params !== 'object') {
        return url;
    }

    const newUrl = url.split('?').filter(Boolean).join('?');

    const idx = newUrl.indexOf('?');
    const base = idx > -1
        ? newUrl.slice(0, idx)
        : newUrl;

    return base
        .concat('?')
        .concat(stringify({
            ...getQueryParamsFromUrl(newUrl),
            ...params,
        }));
}

/**
 * @param {String} url
 * @param {Object} params - an object full of query params
 * @return {String} `url`, with new query params merged into existing query params if any
 */
export {
    buildAbsoluteUrl,
    buildSearchUrlPath,
    changeLocaleOfUrl,
    formatDateForSearchUrl,
    goBackOrFallback,
    makeQueryString,
    removeLanguagePath,
    uriDecodeObjectValues,
};

/**
 * @param {String} url
 * @param {String} newLocale
 * @return {String} `url`, with the language updated to `newLocale`
 */
function changeLocaleOfUrl(url, newLocale) {
    if (!url || !newLocale) {
        return url;
    }

    const newPath = url.split('/')
        .filter(Boolean)
        .map((token, tokenIndex) => {
            return !tokenIndex
                ? newLocale
                : token;
        })
        .join('/');

    return `/${newPath}`;
}

function buildAbsoluteUrl(opts) {
    let {
        language,
        href,
        queries = {},
        allowCrossDomainProtocolRelative = true,
    } = opts;

    queries = typeof queries === 'string'
        ? parse(queries)
        : queries;

    let baseAddress = href;
    let urlQueryString = null;
    let anchorTag = '';
    if (href.indexOf('?') !== -1) {
        [
            baseAddress,
            urlQueryString,
        ] = href.split('?');
    }

    const queryString = makeQueryString({
        ...queries,
        ...parse(urlQueryString),
    });

    const isExternal = baseAddress.indexOf('/') === -1
        || baseAddress.indexOf('http') !== -1
        || baseAddress.indexOf('https') !== -1;
    let isProtocolRelative = baseAddress.slice(0, 2) === '//';
    const isRelative = baseAddress.slice(0, 1) !== '/';

    if (!isExternal && baseAddress.slice(-1) !== '/' && !baseAddress.includes('#')) {
        baseAddress += '/';
    }

    if (baseAddress.includes('#')) {
        [
            baseAddress,
            anchorTag,
        ] = href.split('#');

        anchorTag = `#${anchorTag}`;
    }

    if (isProtocolRelative) {
        const domain = baseAddress.slice(2).split('/')[0];
        const isCrossDomain = !domain.endsWith('wanderu.com');
        if (isCrossDomain && !allowCrossDomainProtocolRelative) {
            baseAddress = baseAddress.slice(1);
            isProtocolRelative = false;
        }
    }

    if (isProtocolRelative || isRelative || isExternal) {
        return baseAddress + queryString + anchorTag;
    }

    const languagePath = language
        ? `/${language}`
        : '';
    return languagePath + baseAddress + queryString + anchorTag;
}

function formatDateForSearchUrl(date) {
    return moment.parseZone(date).format('YYYY-MM-DD');
}

function buildSearchUrlPath(searchRequest, queries) {
    const {
        departDate,
        departPlace,
        arrivePlace,
        returnDate,
        departPlaceId,
        arrivePlaceId,
    } = searchRequest;

    let url = '/depart/';
    url += [departPlace, arrivePlace]
        .reduce((urlPart, input) => {
            return `${urlPart}${encodeURIComponent(input)}/`;
        }, '');

    url += [departDate, returnDate]
        .filter(Boolean)
        .reduce((urlPart, date) => {
            return `${urlPart}${formatDateForSearchUrl(date)}/`;
        }, '');

    const queryString = `?${qs.stringify({
        ...queries,
        dpid: arrivePlaceId,
        opid: departPlaceId,
    })}`;
    return `${url}${queryString}`;
}

function makeQueryString(queries) {
    if (typeof queries === 'string') {
        return queries;
    }

    if (typeof queries === 'object') {
        const cleanedQueries = cleanQueriesObj(queries);
        if (Object.keys(cleanedQueries).length > 0) {
            return '?' + stringify(cleanedQueries, { strict: false });
        }
    }

    return '';
}

function cleanQueriesObj(queries) {
    return Object.entries(queries)
        .filter(([, value]) => value !== undefined)
        .reduce((obj, [key, value]) => {
            return { ...obj, [key]: value };
        }, {});
}

/**
 * @param {String} path
 * @return {String} `path`, with no locale
 */
function removeLanguagePath(path = '') {
    const newPath = path.split('/')
        .slice(2) // first entry is an empty string, second is the locale string
        .join('/');

    return `/${newPath}`;
}

function uriDecodeObjectValues(obj) {
    if (!obj) {
        return obj;
    }
    return Object.entries(obj).reduce((acc, [key, value]) => {
        if (!value) {
            return acc;
        }

        return {
            ...acc,
            [key]: decodeURI(value),
        };
    }, {});
}

export function getQueryParamsFromUrl(url) {
    const idx = url.indexOf('?');

    return idx > -1
        ? parse(url.slice(idx))
        : {};
}
